import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Modal } from '../../components/Modal';
import { ConfirmModal } from '../../components/ConfirmModal';
import { useToast } from '../../components/Toast';
import { UpperInput } from '../../components/inputs/UpperInput';
import { useStickyHeader } from '../../lib/useStickyHeader';
import { MenuAcoesLinha, type AcaoLinha } from '../../components/MenuAcoesLinha';
import { StatusBadge } from '../../components/StatusBadge';
import { Pencil, Trash2 } from 'lucide-react';
import { parseMetaMarkup } from '@fiodecobre/shared';
import { useAuth } from '../../lib/auth';

interface Item {
  id: number;
  nome: string;
  status: string;
  markup_meta_pct?: string | number | null;
  // So Categorias: quantos produtos apontam pra ela (a rota manda quando a entidade tem fkEmProdutos).
  produtos_ativos?: number;
  produtos_inativos?: number;
}

interface Props {
  titulo: string;
  recurso: string; // ex "categorias" / "grupos" / "marcas"
  ativoVal: string; // 'ativa' | 'ativo'
  inativoVal: string;
  singular?: string; // rotulo singular pro modal (default: titulo sem o ultimo caractere)
  // So admin cria, edita e remove — o servidor recusa os outros perfis, entao a tela nem oferece.
  soAdmin?: boolean;
  // Meta de Margem (Categorias — a mesma coluna da tela Metas de Margem): coluna no grid pra todos;
  // campo no modal so pro admin. Vazio = segue a meta padrao da loja.
  comMeta?: boolean;
  // Coluna "Produtos" (ativos; inativos em cinza ao lado).
  comProdutos?: boolean;
}

const txtMeta = (v: string | number | null | undefined) =>
  v == null || v === '' ? '' : Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 2 });

export function ListaSimples({ titulo, recurso, ativoVal, inativoVal, singular, soAdmin, comMeta, comProdutos }: Props) {
  const nomeSingular = singular ?? titulo.slice(0, -1);
  const toast = useToast();
  const { usuario } = useAuth();
  const ehAdmin = usuario?.perfil === 'admin';
  const podeEscrever = !soAdmin || ehAdmin;
  const mostraMeta = !!comMeta && ehAdmin;
  const [metaTxt, setMetaTxt] = useState('');
  const [metaPadrao, setMetaPadrao] = useState<number | null>(null);
  const metaNova = parseMetaMarkup(metaTxt);
  const [itens, setItens] = useState<Item[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState('');
  const [mostrarInativos, setMostrarInativos] = useState(false);
  const [editando, setEditando] = useState<Item | null>(null);
  const [novoModal, setNovoModal] = useState(false);
  const [textoForm, setTextoForm] = useState('');
  const [confirmarRemover, setConfirmarRemover] = useState<Item | null>(null);

  async function carregar() {
    setCarregando(true);
    try {
      const lista = await api.get<Item[]>(`/api/${recurso}`, { query: { incluir_inativos: mostrarInativos } });
      setItens(lista);
    } catch (e: any) {
      toast.erro(e.message ?? 'falha ao carregar');
    } finally {
      setCarregando(false);
    }
  }

  useEffect(() => { void carregar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [mostrarInativos]);

  // Meta padrao da loja, pra dizer (no grid e no campo) o que "vazio" significa. A rota e' aberta a todo perfil.
  useEffect(() => {
    if (!comMeta) return;
    api.get<{ padrao: number }>('/api/margens/mapa').then((r) => setMetaPadrao(r.padrao)).catch(() => {});
  }, [comMeta]);

  const filtrados = itens.filter((it) => it.nome.toLowerCase().includes(busca.toLowerCase()));

  function abrirNovo() {
    setEditando(null);
    setTextoForm('');
    setMetaTxt('');
    setNovoModal(true);
  }
  function abrirEditar(it: Item) {
    setEditando(it);
    setTextoForm(it.nome);
    setMetaTxt(txtMeta(it.markup_meta_pct));
    setNovoModal(true);
  }

  async function salvar() {
    if (!textoForm.trim()) return;
    if (mostraMeta && metaNova === undefined) return;
    const corpo: Record<string, unknown> = { nome: textoForm };
    if (mostraMeta) corpo.meta = metaNova;
    try {
      if (editando) {
        await api.put(`/api/${recurso}/${editando.id}`, corpo);
        toast.sucesso('atualizado');
      } else {
        await api.post(`/api/${recurso}`, corpo);
        toast.sucesso('criado');
      }
      setNovoModal(false);
      void carregar();
    } catch (e: any) {
      toast.erro(e.message ?? 'falha ao salvar');
    }
  }

  async function remover() {
    if (!confirmarRemover) return;
    try {
      await api.delete(`/api/${recurso}/${confirmarRemover.id}`);
      toast.sucesso('removido');
      setConfirmarRemover(null);
      void carregar();
    } catch (e: any) {
      toast.erro(e.message ?? 'falha ao remover');
    }
  }

  const sticky = useStickyHeader();

  return (
    <div>
      <div ref={sticky.headerRef} style={sticky.headerStyle}>
        <div className="pagina-header">
          <h2 className="pagina-titulo">{titulo}</h2>
          <div className="pagina-acoes">
            <input
              type="search"
              placeholder="Buscar..."
              className="busca"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
              <input type="checkbox" checked={mostrarInativos} onChange={(e) => setMostrarInativos(e.target.checked)} />
              Mostrar inativos
            </label>
            {podeEscrever && <button type="button" className="btn-primario" onClick={abrirNovo}>+ Novo</button>}
          </div>
        </div>
      </div>

      {carregando ? (
        <p className="muted">Carregando...</p>
      ) : filtrados.length === 0 ? (
        <p className="muted">Nenhum registro.</p>
      ) : (
        <table className="tabela" style={sticky.tableStyle}>
          <thead>
            <tr>
              <th style={comMeta ? { ...sticky.thStyle, width: 500 } : sticky.thStyle}>Nome</th>
              {comMeta && <th style={sticky.thStyle}>Meta de Margem</th>}
              {comProdutos && <th style={{ ...sticky.thStyle, width: 160, textAlign: 'right' }}>Produtos</th>}
              <th style={{ ...sticky.thStyle, width: 100 }}>Status</th>
              {podeEscrever && <th style={{ ...sticky.thStyle, width: 80, textAlign: 'right' }}>Acoes</th>}
            </tr>
          </thead>
          <tbody>
            {filtrados.map((it) => {
              const acoes: AcaoLinha[] = [
                { label: 'Editar', icon: Pencil, onClick: () => abrirEditar(it) },
                { label: 'Remover', icon: Trash2, perigoso: true, onClick: () => setConfirmarRemover(it) },
              ];
              const temMeta = it.markup_meta_pct != null && it.markup_meta_pct !== '';
              return (
                <tr key={it.id} onDoubleClick={podeEscrever ? () => abrirEditar(it) : undefined}>
                  <td>{it.nome}</td>
                  {comMeta && (
                    <td>
                      {temMeta
                        ? <strong>{txtMeta(it.markup_meta_pct)}%</strong>
                        : <span className="muted">segue a padrão{metaPadrao != null ? ` (${txtMeta(metaPadrao)}%)` : ''}</span>}
                    </td>
                  )}
                  {comProdutos && (
                    <td style={{ textAlign: 'right' }}>
                      {it.produtos_ativos ?? 0}
                      {!!it.produtos_inativos && <span className="muted" style={{ fontSize: 12 }}> (+{it.produtos_inativos} inativo{it.produtos_inativos > 1 ? 's' : ''})</span>}
                    </td>
                  )}
                  <td><StatusBadge ativo={it.status === ativoVal} /></td>
                  {podeEscrever && (
                    <td style={{ textAlign: 'right' }}>
                      <MenuAcoesLinha acoes={acoes} />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <Modal
        aberto={novoModal}
        onFechar={() => setNovoModal(false)}
        titulo={editando ? `Editar ${nomeSingular}` : `Novo ${nomeSingular}`}
        largura={460}
        rodape={
          <>
            <button type="button" className="btn-secundario" onClick={() => setNovoModal(false)}>Cancelar</button>
            <button type="button" className="btn-primario" onClick={salvar}
              disabled={!textoForm.trim() || (mostraMeta && metaNova === undefined)}>Salvar</button>
          </>
        }
      >
        <div className="form-grid">
          <label className="full">
            Nome
            <UpperInput value={textoForm} onChange={setTextoForm} autoFocus />
          </label>
          {mostraMeta && (
            <label className="full">
              Meta de Margem (%)
              <input
                type="text" inputMode="decimal" value={metaTxt} onChange={(e) => setMetaTxt(e.target.value)}
                placeholder={`Vazio = segue a meta padrão da loja${metaPadrao != null ? ` (${txtMeta(metaPadrao)}%)` : ''}`}
                style={{ maxWidth: 320 }}
              />
              <span className="muted" style={{ fontSize: 12 }}>
                {metaNova === undefined
                  ? 'Meta inválida: use um % de 0 a 1000, ou deixe vazio.'
                  : 'Markup sobre o custo, a mesma conta do Mrg Pix — também editável em Metas de Margem.'}
              </span>
            </label>
          )}
        </div>
      </Modal>

      <ConfirmModal
        aberto={!!confirmarRemover}
        titulo="Remover"
        mensagem={`Confirma remover "${confirmarRemover?.nome}"? Vai ficar marcado como ${inativoVal}.`}
        textoConfirmar="Remover"
        perigoso
        onConfirmar={remover}
        onCancelar={() => setConfirmarRemover(null)}
      />
    </div>
  );
}
